// 업데이트 소식 목록 — "뭐가 바뀌었나"를 훑어보는 화면.
//
// 이 화면의 핵심은 글이 아니라 **전/후 그림 두 장**이다. 사용자는 설명을 읽기 전에 그림부터
// 본다: 왼쪽이 예전 화면(요청한 사람이 불편한 자리를 찍어 보낸 것, 그 자리에 표시가 구워져 있다),
// 오른쪽이 지금 화면. 두 장을 나란히 놓는 것만으로 "아, 저기가 저렇게 바뀌었구나"가 끝난다.
// 그래서 글은 일부러 짧다(제목 한 줄 + 불릿 2~3개).

import { useState } from "react";
import { CATEGORY_LABEL, type WhatsNewItem, type WhatsNewTicket } from "../../lib/whatsNew";
import { SkeletonList } from "../common/Skeleton";

const CATEGORY_STYLE: Record<string, string> = {
  new: "bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400",
  improve: "bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400",
  fix: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const sameYear = d.getFullYear() === today.getFullYear();
  return d.toLocaleDateString("ko-KR", {
    ...(sameYear ? {} : { year: "numeric" }),
    month: "long",
    day: "numeric",
  });
}

/** `- 한 줄` 불릿 문자열을 줄 목록으로. (마크다운 렌더러를 들이지 않으려는 최소 처리) */
function bulletsOf(summary: string): string[] {
  return summary
    .split("\n")
    .map((l) => l.replace(/^[-*]\s*/, "").trim())
    .filter(Boolean);
}

/** 그림 한 장 + 그게 언제 화면인지(전/후) 라벨. 누르면 크게 본다. */
function Shot({ src, label, tone, onZoom }: { src: string; label: string; tone: "before" | "after"; onZoom: () => void }) {
  return (
    <figure className="min-w-0 flex-1">
      <figcaption
        className={`mb-1.5 inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold ${
          tone === "before"
            ? "bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-400"
            : "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400"
        }`}
      >
        {label}
      </figcaption>
      <button
        type="button"
        onClick={onZoom}
        title="크게 보기"
        className="block w-full overflow-hidden rounded-xl border border-gray-200 bg-gray-50 transition hover:border-brand-400 dark:border-gray-700 dark:bg-gray-800"
      >
        <img src={src} alt={label} loading="lazy" className="max-h-56 w-full object-cover object-top" />
      </button>
    </figure>
  );
}

/**
 * 요청 원문의 `**굵게**` 표기만 실제 굵은 글씨로 바꾼다.
 *
 * 요청은 사람이 쓴 글이라 강조가 별표로 들어오는 일이 흔한데, 그대로 두면 `**여기가 문제다**`
 * 처럼 별표가 그대로 보여 읽기가 나빠진다. 그렇다고 마크다운 렌더러를 통째로 들이면 이 한
 * 군데를 위해 남의 글을 HTML 로 해석하게 되므로(요청 글은 우리가 쓴 게 아니다) **강조 하나만**
 * 다룬다. 나머지 표기는 글자 그대로 두는 편이 안전하고, 실제로도 강조 말고는 거의 안 쓰인다.
 */
function renderEmphasis(text: string) {
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    // split 의 홀수 자리가 괄호로 잡힌 강조 부분이다.
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold text-gray-800 dark:text-white/90">
        {part}
      </strong>
    ) : (
      part
    ),
  );
}

/**
 * 이 변화를 만든 요청 — 접어 둔 원문.
 *
 * 위쪽 소식은 "무엇이 좋아졌나"만 남긴 짧은 글이라 **왜 이걸 고쳤는지가 빠져 있다.** 그 대목이
 * 궁금한 사람에게만 보이면 된다: 기본은 접어 두고, 펼치면 요청자가 쓴 글이 그대로 나온다.
 * 요청 글은 길고 문단이 나뉘어 있어 줄바꿈을 살려야 읽힌다(`whitespace-pre-wrap`).
 *
 * ⚠️ **기본으로 펼쳐 두지 않는다.** 이 화면의 주인공은 전/후 그림이고, 요청 원문은 길어서
 * 펼쳐 두면 소식 목록이 통째로 글밭이 된다 — 훑어보는 화면의 성격이 사라진다.
 */
function TicketDetail({ ticket }: { ticket: WhatsNewTicket }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-4 border-t border-gray-100 pt-3 dark:border-gray-800">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 text-left text-xs font-semibold text-gray-500 transition hover:text-brand-600 dark:text-gray-400 dark:hover:text-brand-400"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
        <span>{open ? "요청 내용 접기" : "이 변화를 요청한 내용 보기"}</span>
        <span className="ml-auto shrink-0 rounded-md bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-500 dark:bg-white/10 dark:text-gray-400">
          요청 #{ticket.no}
        </span>
      </button>

      {open && (
        <div className="mt-3 rounded-xl bg-gray-50 p-4 dark:bg-gray-950/60">
          <p className="text-[13px] font-bold leading-snug text-gray-700 dark:text-gray-200">{ticket.title}</p>
          <p className="mt-0.5 text-[11px] text-gray-400">{formatDate(ticket.requestedAt)}에 요청</p>
          {ticket.request && (
            <p className="mt-3 whitespace-pre-wrap text-[13px] leading-relaxed text-gray-600 dark:text-gray-300">
              {renderEmphasis(ticket.request)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Card({ item }: { item: WhatsNewItem }) {
  const [zoom, setZoom] = useState<string | null>(null);
  const before = item.before[0];
  const after = item.after[0];

  return (
    <article className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
      <header className="mb-2 flex flex-wrap items-center gap-2">
        <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${CATEGORY_STYLE[item.category] ?? CATEGORY_STYLE.improve}`}>
          {CATEGORY_LABEL[item.category] ?? "개선"}
        </span>
        <time className="ml-auto text-xs text-gray-400">{formatDate(item.publishedAt)}</time>
      </header>

      <h3 className="text-[15px] font-bold leading-snug text-gray-800 dark:text-white/90">{item.title}</h3>

      <ul className="mt-2 flex flex-col gap-1">
        {bulletsOf(item.summary).map((b, i) => (
          <li key={i} className="flex gap-1.5 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
            <span className="text-gray-300 dark:text-gray-600">·</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>

      {(before || after) && (
        <div className={`mt-4 flex flex-col gap-3 sm:flex-row sm:gap-4 ${before && after ? "" : "sm:max-w-sm"}`}>
          {before && <Shot src={before} label="변경 전" tone="before" onZoom={() => setZoom(before)} />}
          {after && <Shot src={after} label="변경 후" tone="after" onZoom={() => setZoom(after)} />}
        </div>
      )}

      {item.ticket && <TicketDetail ticket={item.ticket} />}

      {/* 크게 보기 — 그림을 눌렀을 때만. 화면 어디를 눌러도 닫힌다(볼 것만 보고 나가는 창). */}
      {zoom && (
        <div
          role="dialog"
          aria-label="크게 보기"
          onClick={() => setZoom(null)}
          className="fixed inset-0 lnb-inset z-[100000] flex items-center justify-center bg-black/70 p-4"
        >
          <img src={zoom} alt="" className="max-h-full max-w-full rounded-lg object-contain" />
        </div>
      )}
    </article>
  );
}

export default function WhatsNewFeed({ items, loading }: { items: WhatsNewItem[]; loading?: boolean }) {
  if (loading && !items.length) {
    return <SkeletonList rows={4} className="py-10" />;
  }
  if (!items.length) {
    return (
      <p className="rounded-xl border border-dashed border-gray-300 py-10 text-center text-sm text-gray-400 dark:border-gray-700">
        아직 전해 드릴 소식이 없습니다.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {items.map((item) => (
        <Card key={item.id} item={item} />
      ))}
    </div>
  );
}
